import { type StreamingMessage, useStreamingLanguage, streamLocal, streamText, streamFeedback, streamMessage } from "../i18n/streamingSettingsPresentation";
import { useCallback, useEffect, useRef, useState } from "react";

import { useMe } from "./useMe";
import { getCsrfToken } from "../lib/csrf";

// ── Types ────────────────────────────────────────────────────────────────────

/** Mirrors the backend UserPreferences shape from userPreferencesService.ts */
export type UserPreferences = {
  version: 1;
  softwareQuickNavCategoryOrder: string[] | null;
  wsdViewMode: "categorical" | "cards" | "table" | null;
  wsdMediaDensity: number | null;
  /** Poster density on the Media Requests surface. Independent of `wsdMediaDensity`. */
  wsdRequestsDensity: number | null;
  /** Persisted media-type filter for the Top Watched panel. null = all types. */
  wsdTopWatchedMediaTypes: string[] | null;
  /** Persisted Asset Management Center view mode. null = default (card). */
  assetViewMode: "card" | "list" | null;
  /** Per-user consent to operate apps over a linked chat channel. null/false = off. */
  commandChannelOptIn: boolean | null;
  /**
   * UI hint: whether the user has set a custom noVNC web-desktop password.
   * null/false = still on the install default. Never the secret itself — the
   * backend persists only this boolean flag alongside the set operation.
   */
  novncCustomPasswordSet: boolean | null;
};

type PreferencesState = {
  preferences: UserPreferences;
  loading: boolean;
  saving: boolean;
  error: string | StreamingMessage | undefined;
};

type PreferencePatch = {
  softwareQuickNavCategoryOrder?: string[] | null;
  wsdViewMode?: "categorical" | "cards" | "table" | null;
  wsdMediaDensity?: number | null;
  wsdRequestsDensity?: number | null;
  wsdTopWatchedMediaTypes?: string[] | null;
  assetViewMode?: "card" | "list" | null;
  commandChannelOptIn?: boolean | null;
};

// ── Defaults ─────────────────────────────────────────────────────────────────

const createDefaults = (): UserPreferences => ({
  version: 1,
  softwareQuickNavCategoryOrder: null,
  wsdViewMode: null,
  wsdMediaDensity: null,
  wsdRequestsDensity: null,
  wsdTopWatchedMediaTypes: null,
  assetViewMode: null,
  commandChannelOptIn: null,
  novncCustomPasswordSet: null,
});

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useUserPreferences = () => {
  useStreamingLanguage();
  const { user, csrfToken } = useMe();

  const [state, setState] = useState<PreferencesState>({
    preferences: createDefaults(),
    loading: true,
    saving: false,
    error: undefined,
  });

  // Track latest save promise to avoid stale overwrites
  const saveCounter = useRef(0);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const reload = useCallback(
    async (options?: { signal?: AbortSignal }) => {
      if (!user) {
        setState((prev) => ({
          ...prev,
          preferences: createDefaults(),
          loading: false,
          error: undefined,
        }));
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: undefined }));

      try {
        const res = await fetch("/api/me/preferences", {
          headers: { Accept: "application/json" },
          cache: "no-store",
          signal: options?.signal,
        });

        if (options?.signal?.aborted) return;

        if (res.ok) {
          const payload = (await res.json().catch(() => ({}))) as {
            ok?: boolean;
            data?: { preferences?: UserPreferences };
          };
          const prefs = payload?.data?.preferences ?? createDefaults();
          setState((prev) => ({
            ...prev,
            preferences: { ...createDefaults(), ...prefs, version: 1 },
            loading: false,
            error: undefined,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: streamLocal("unableToLoadPreferences"),
          }));
        }
      } catch (err) {
        if (options?.signal?.aborted) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: streamLocal("unableToLoadPreferences"),
        }));
      }
    },
    [user]
  );

  useEffect(() => {
    const controller = new AbortController();
    reload({ signal: controller.signal });
    return () => controller.abort();
  }, [reload]);

  // ── Update (PATCH) ───────────────────────────────────────────────────────

  const update = useCallback(
    async (
      patch: PreferencePatch
    ): Promise<{ ok: boolean; error?: string }> => {
      const seq = ++saveCounter.current;

      setState((prev) => ({ ...prev, saving: true }));

      // Optimistically apply locally
      setState((prev) => {
        const next = { ...prev.preferences };
        if (patch.softwareQuickNavCategoryOrder !== undefined) {
          next.softwareQuickNavCategoryOrder =
            patch.softwareQuickNavCategoryOrder;
        }
        if (patch.wsdViewMode !== undefined) {
          next.wsdViewMode = patch.wsdViewMode;
        }
        if (patch.wsdMediaDensity !== undefined) {
          next.wsdMediaDensity = patch.wsdMediaDensity;
        }
        if (patch.wsdRequestsDensity !== undefined) {
          next.wsdRequestsDensity = patch.wsdRequestsDensity;
        }
        if (patch.wsdTopWatchedMediaTypes !== undefined) {
          next.wsdTopWatchedMediaTypes = patch.wsdTopWatchedMediaTypes;
        }
        if (patch.assetViewMode !== undefined) {
          next.assetViewMode = patch.assetViewMode;
        }
        if (patch.commandChannelOptIn !== undefined) {
          next.commandChannelOptIn = patch.commandChannelOptIn;
        }
        return { ...prev, preferences: next };
      });

      try {
        const csrf = csrfToken ?? getCsrfToken();
        const res = await fetch("/api/me/preferences", {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify(patch),
        });

        const payload = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          data?: { preferences?: UserPreferences };
          message?: string;
        };

        // Only apply server response if this is still the latest save
        if (seq !== saveCounter.current) {
          return { ok: res.ok };
        }

        if (res.ok && payload.data?.preferences) {
          setState((prev) => ({
            ...prev,
            preferences: {
              ...createDefaults(),
              ...payload.data!.preferences,
              version: 1,
            },
            saving: false,
            error: undefined,
          }));
          return { ok: true };
        }

        // Non-OK: revert is not needed since next fetch will reconcile
        setState((prev) => ({
          ...prev,
          saving: false,
          error: payload.message ?? streamLocal("unableToSavePreferences"),
        }));
        return { ok: false, error: payload.message ?? streamText("unableToSavePreferences") };
      } catch (err) {
        if (seq !== saveCounter.current) return { ok: false };
        const message =
          streamFeedback(err, "unableToSavePreferences");
        setState((prev) => ({ ...prev, saving: false, error: message }));
        return { ok: false, error: streamMessage(message) ?? undefined };
      }
    },
    [csrfToken]
  );

  return {
    preferences: state.preferences,
    loading: state.loading,
    saving: state.saving,
    error: streamMessage(state.error ?? null) ?? undefined,
    update,
    reload,
  };
};
